import animationData from "animation/propGoAnimation"
import { Button, Dropdown, Tag, notification } from "components/common"
import { useAddClientContactViewTrackingMutation, useCallViaPropGoMutation } from "features/clients/detail/api"
import { useLazyGetClientCellsQuery, useLazyGetExternalClientCellsQuery } from "features/shared/api"
import { useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { fireEvent } from "utils/gtm/gtmEventHandler"
import { ACTION_TYPES } from "utils/gtm/gtmEventsData"
import Tooltip from "../../../components/common/tooltip/tooltip"
import SalesPlaybookDrawer from "../../leads/components/salesPlaybook"
import { configs } from "configs"

const CallButton = props => {
  const { t } = useTranslation()
  const {
    client,
    affiliate,
    disabled,
    leadId,
    from,
    className,
    preFetchedNumber = false,
    staffUser = false,
    willTriggerSalesPlaybook = true,
    ...buttonProps
  } = props
  const userInfo = useSelector(state => state.app.user?.info)
  const salesPlayBookDrawerRef = useRef()
  const [dropdownOpen, setDropdownOpen] = useState(false)

  const defaultOptions = { loop: true, autoplay: true, animationData: animationData }
  const [getClientCellInfo, { data: clientCells, isFetching: fetchClientCell }] = useLazyGetClientCellsQuery()
  const [callViaPropGo, options] = useCallViaPropGoMutation()
  const [addClientContactView] = useAddClientContactViewTrackingMutation()
  const [getExternalClientInfo, { data: externalCells, isFetching: fetchExternalClientCell }] =
    useLazyGetExternalClientCellsQuery()

  const { isFetching: isFetchingCall, isError, error } = options

  const callAndReset = (cellNumber = preFetchedNumber, id) => {
    callViaPropGo({
      cellNumber: cellNumber,
      ...(!!staffUser ? { userId: id } : { clientId: id }),
      ...(affiliate && { affiliate: true }),
    })
      .unwrap()
      .then(() => {
        configs?.isSalesPlaybookEnabled && willTriggerSalesPlaybook && salesPlayBookDrawerRef.current?.openDrawer({ clientId: client?.id, triggeredBy: "callButton" })
      })
      .catch(e => {
        notification.error({ message: e.message })
      })

    fireEvent({
      ...ACTION_TYPES.call_contact_client,
      user_input: cellNumber,
      client_id: client?.id,
      ...(leadId && { lead_id: leadId }),
      ...from,
    })
  }

  const onClick = e => {
    e.domEvent.stopPropagation()
    setDropdownOpen(false)
    if (affiliate) {
      callAndReset(items[e.key].value, affiliate?.id)
    } else {
      callAndReset(clientCells?.[e.key]?.number, client?.id)
    }
  }

  const onCopy = e => {
    e.preventDefault()
  }
  const onContextMenu = e => {
    e.preventDefault()
  }

  const items = useMemo(() => {
    if (preFetchedNumber) {
      return [{ label: preFetchedNumber, key: "preFetched", value: preFetchedNumber }]
    }

    if (affiliate) {
      return affiliate?.affiliateCells?.map((e, index) => {
        return {
          label: (
            <div className="d-flex flexYcenter" style={{ gap: "6px" }}>
              {e?.number ?? e?.cell}
              <Tooltip title={e.lastUsedDateTime}>
                <Tag pill style={{ color: "#0091ba", "--tag-bg": "#E6F5F9" }}>
                  {e.lastUsed ? "Last Called" : ""}
                </Tag>
              </Tooltip>
            </div>
          ),
          key: index,
          value: e?.number ?? e?.cell,
        }
      })
    } else {
      return (from === "external" ? externalCells : clientCells)?.map((e, index) => {
        if (from === "external") return { label: e, key: index, value: e }
        else {
          return {
            label: (
              <div className="d-flex flexYcenter" style={{ gap: "6px" }}>
                {e.number}
                <Tooltip title={e.lastUsedDateTime}>
                  <Tag pill style={{ color: "#0091ba", "--tag-bg": "#E6F5F9" }}>
                    {e.lastUsed}
                  </Tag>
                </Tooltip>
              </div>
            ),
            key: index,
            value: e?.number,
          }
        }
      })
    }
  }, [clientCells, affiliate, externalCells, preFetchedNumber])

  return (
    <>
      {/* Main Dropdown Button */}
      <Dropdown
        overlayClassName="ddPhone"
        menu={{ items, onClick, onCopy, onContextMenu }}
        dropdownRender={menu => (items?.length ? <div className="dropdown-content">{menu}</div> : <></>)}
        placement="bottomLeft"
        suffixIcon=""
        disabled={disabled}
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        destroyPopupOnHide
      >
        <Button
          loading={!preFetchedNumber && (fetchClientCell || fetchExternalClientCell)}
          className={className}
          icon={"IconGo"}
          iconProps={{ size: "1.6em" }}
          onClick={e => {
            e.stopPropagation()
            if (!preFetchedNumber) {
              if (client && from !== "external") {
                getClientCellInfo({ clientId: client?.id, actionType: "tooltip&includeLastUsed=true" }, true)
              }
              if (from === "external") {
                getExternalClientInfo({ clientId: client?.id, actionType: "tooltip", type: "propgo" })
              }
            }
            setDropdownOpen(true)
          }}
          content={t("Call via PropGo")}
          color="#0186fe"
          shape="round"
          type="light"
          disabled={!userInfo?.AgencyConfiguration?.Platform?.platformKey === "zameen" || disabled}
          {...buttonProps}
        />
      </Dropdown>
      {willTriggerSalesPlaybook && <SalesPlaybookDrawer refs={salesPlayBookDrawerRef} />}
    </>
  )
}

export default CallButton
